import ApiService from './apiService';

/**
 * Service pour gérer les opérations liées aux commandes fournisseurs (Purchase Orders)
 */
class PurchaseOrderService {
    /**
     * Récupérer la liste des commandes avec filtres et pagination
     * @param {Object} params Paramètres: search, status, supplier_id, per_page, page, etc.
     * @returns {Promise} Réponse API (paginateur Laravel)
     */
    getPurchaseOrders(params = {}) {
        return ApiService.get('/purchase-orders', params);
    }

    /**
     * Récupérer une commande par ID
     * @param {number|string} id ID de la commande
     * @param {Object} params Paramètres additionnels (relations à charger)
     * @returns {Promise} Réponse API
     */
    getPurchaseOrder(id, params = {}) {
        return ApiService.get(`/purchase-orders/${id}`, params);
    }

    /**
     * Créer une nouvelle commande fournisseur (appliquée automatiquement côté backend)
     * @param {Object} orderData Données de la commande: supplier_id, order_date, notes, items[]
     * @returns {Promise} Réponse API
     */
    createPurchaseOrder(orderData) {
        return ApiService.post('/purchase-orders', orderData);
    }

    /**
     * Mettre à jour le statut/notes d'une commande
     * @param {number|string} id ID de la commande
     * @param {Object} updateData Données à mettre à jour: { status, notes }
     * @returns {Promise} Réponse API
     */
    updatePurchaseOrder(id, updateData) {
        return ApiService.put(`/purchase-orders/${id}`, updateData);
    }

    /**
     * Télécharger une commande fournisseur au format PDF
     * @param {number|string} id ID de la commande à télécharger
     * @returns {Promise} Blob de fichier PDF
     */
    downloadPurchaseOrder(id) {
        return ApiService.post(`/purchase-orders/${id}/download`, {}, { responseType: 'blob' });
    }

    /**
     * Préparer un payload de commande à partir d'un objet form
     * Convertit proprement les champs numériques.
     * @param {Object} form
     * @returns {Object} Payload prêt pour l'API
     */
    prepareOrderPayload(form) {
        const items = (form.items || []).map(it => ({
            product_id: it.product_id,
            quantity: Number(it.quantity),
            purchase_price: Number(it.purchase_price),
            sale_price: it.sale_price !== undefined && it.sale_price !== null && it.sale_price !== '' ? Number(it.sale_price) : null,
            discount: it.discount !== undefined && it.discount !== null && it.discount !== '' ? Number(it.discount) : 0
        }));

        return {
            supplier_id: form.supplier_id,
            order_date: form.order_date,
            notes: form.notes || null,
            items
        };
    }


    async printPurchaseOrder(purchaseOrderId, open = false) {
        if (!purchaseOrderId) throw new Error('ID de commande fournisseur requis');

        const response = await this.downloadPurchaseOrder(purchaseOrderId);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        if (open) {
            // Ouvrir dans un nouvel onglet pour prévisualisation
            window.open(url, '_blank');
        } else {
            // Télécharger directement
            const link = document.createElement('a');
            link.href = url;
            link.download = `commande-fournisseur-${purchaseOrderId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        // Nettoyer l'URL après un délai pour permettre l'ouverture
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    }

    /**
     * Annuler une commande fournisseur via l'API.
     * @param {number|string} purchaseOrderId
     * @returns {Promise<Object>} réponse de l'API
     */
    async cancelPurchaseOrder(purchaseOrderId) {
        if (!purchaseOrderId) throw new Error('ID de commande fournisseur requis');
        return await ApiService.post(`/purchase-orders/${purchaseOrderId}/cancel`);
    }
}

export default new PurchaseOrderService();


